import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw, Search, CheckCircle, Clock,
  XCircle, DollarSign, FileText, ArrowUpRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';
import WithdrawalReceipt from '../components/WithdrawalReceipt';

const P  = '#C29500';
const P2 = '#E8C040';
const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};

const STATUS_CFG = {
  completed  : { color: G,         icon: <CheckCircle size={11}/>, label: 'Completed'  },
  processing : { color: C,         icon: <Clock size={11}/>,       label: 'Processing' },
  pending    : { color: '#F59E0B', icon: <Clock size={11}/>,       label: 'Pending'    },
  failed     : { color: R,         icon: <XCircle size={11}/>,     label: 'Failed'     },
  rejected   : { color: R,         icon: <XCircle size={11}/>,     label: 'Rejected'   },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
      color: cfg.color,
      background: `${cfg.color}14`,
      border: `1px solid ${cfg.color}30`,
    }}>
      {cfg.icon}{cfg.label}
    </span>
  );
}

const FILTERS = ['all','pending','processing','completed','rejected','failed'];

export default function WithdrawalHistory() {
  const [withdrawals,    setWithdrawals]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [filter,         setFilter]         = useState('all');
  const [receipt,        setReceipt]        = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/wallet/withdrawals');
      setWithdrawals(res.data || []);
    } catch {
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const viewReceipt = async (id) => {
    setLoadingReceipt(id);
    try {
      const res = await api.get(`/wallet/withdrawals/${id}/receipt`);
      setReceipt(res.data);
    } catch {
      toast.error('Failed to load receipt');
    } finally {
      setLoadingReceipt(null);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

  const filtered = withdrawals.filter(w => {
    const matchSearch = (w.tx_ref || '').toLowerCase().includes(search.toLowerCase()) ||
                        (w.method || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || w.status === filter;
    return matchSearch && matchFilter;
  });

  const totalWithdrawn = withdrawals.filter(w => w.status === 'completed').reduce((s, w) => s + (w.amount_usdt || 0), 0);
  const pending        = withdrawals.filter(w => w.status === 'pending').length;

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 36, height: 36, border: `3px solid ${P}`,
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin .7s linear infinite', margin: '0 auto 12px',
          }}/>
          <p style={{ color: '#475569', fontSize: 14 }}>Loading payouts…</p>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 28, maxWidth: 1200, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: -0.5 }}>Payouts</h1>
            <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0 0' }}>Track all your withdrawal requests</p>
          </div>
          <motion.button whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}
            onClick={loadData}
            style={{
              padding: 10, borderRadius: 10, cursor: 'pointer',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              color: '#64748B', display: 'flex', alignItems: 'center',
            }}>
            <RefreshCw size={16}/>
          </motion.button>
        </motion.div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Total Requests',  value: withdrawals.length,             color: C,         prefix: '' },
            { label: 'Total Withdrawn', value: `$${totalWithdrawn.toFixed(2)}`, color: G,         prefix: '' },
            { label: 'Pending',         value: pending,                         color: '#F59E0B', prefix: '' },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              style={{ ...CARD, padding: 20, textAlign: 'center' }}>
              <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</p>
              <p style={{ fontSize: 24, fontWeight: 900, color: s.color, margin: 0 }}>{s.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by ref or method…"
              style={{
                width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10, padding: '10px 14px 10px 36px', color: '#fff', fontSize: 13,
                outline: 'none', boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = `${P}50`}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
            />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', textTransform: 'capitalize',
                  background: filter === f ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.04)',
                  border: filter === f ? 'none' : '1px solid rgba(255,255,255,0.08)',
                  color: filter === f ? '#000' : '#64748B',
                }}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ ...CARD, overflow: 'hidden' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '56px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14,
                background: 'rgba(255,255,255,0.04)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <DollarSign size={26} style={{ color: '#334155' }}/>
              </div>
              <p style={{ fontSize: 14, color: '#475569', fontWeight: 600, margin: 0 }}>No payouts found</p>
              <p style={{ fontSize: 12, color: '#334155', margin: 0 }}>Your withdrawal history will appear here</p>
            </div>
          ) : (
            <div>
              {filtered.map((w, i) => (
                <motion.div key={w.id || i}
                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  style={{
                    padding: '16px 20px',
                    borderBottom: i < filtered.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>${w.amount_usdt} USDT</span>
                        <StatusBadge status={w.status}/>
                        <span style={{
                          fontSize: 11, color: '#64748B', textTransform: 'uppercase',
                          background: 'rgba(255,255,255,0.04)', padding: '2px 8px', borderRadius: 4,
                        }}>{w.method}</span>
                      </div>
                      {w.method === 'bank' && (
                        <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 4px' }}>
                          {w.bank_name} · {w.account_number} · {w.account_name}
                        </p>
                      )}
                      {w.method === 'momo' && (
                        <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 4px' }}>
                          {w.network} · {w.phone_number}
                        </p>
                      )}
                      <p style={{ fontSize: 11, color: '#334155', margin: 0 }}>Ref: {w.tx_ref} · {formatDate(w.created_at)}</p>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 700, color: P2, margin: 0 }}>
                        {w.currency} {w.amount_local?.toLocaleString()}
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                        onClick={() => viewReceipt(w.id)}
                        disabled={loadingReceipt === w.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                          background: `${P}14`, border: `1px solid ${P}30`,
                          color: P2, fontSize: 12, fontWeight: 600,
                        }}>
                        {loadingReceipt === w.id
                          ? <div style={{ width: 12, height: 12, border: `1.5px solid ${P2}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
                          : <FileText size={12}/>
                        }
                        Receipt
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

      </div>

      {receipt && <WithdrawalReceipt receipt={receipt} onClose={() => setReceipt(null)}/>}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #334155; }
      `}</style>
    </Layout>
  );
}
